import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../../redux/store";
import SiteProgressCarousel from "./SiteProgressCarousel";
import PendingInvoicesComponent from "./PendingInvoices";
import { Avatar, Card, Grid, Section, Skeleton } from "../../../ui";
import { apiGet } from "../../../services/api/apiClient";
import PortalHero from "../../../components/PortalHero";

// ─── Main ClientHome ────────────────────────────────────────────────
const ClientHome = () => {
  const { user } = useAppSelector((state) => state.login);
  const { projects, fetchProjectsStatus, selectedProject } = useAppSelector(
    (state) => state.projects
  );
  const firstName = user?.username?.split(" ")[0] || "";

  // Use selectedProject for display; fall back to first project while loading
  const displayProject: any = selectedProject || projects?.[0] || null;
  const [recent, setRecent] = useState<any[]>([]);

  useEffect(() => {
    const projectId = displayProject?.id;
    if (!projectId) {
      setRecent([]);
      return;
    }
    // Show the latest documents every time the home page opens; the old
    // "since your last visit" filter made the section disappear on reload.
    let cancelled = false;
    apiGet({
      endpoint: "/document-library/recent",
      params: { projectId, limit: 6 },
      withAuth: true,
    })
      .then((data: any) => {
        if (cancelled) return;
        setRecent(data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setRecent([]);
      });
    return () => {
      cancelled = true;
    };
  }, [displayProject?.id]);

  // Extract team members from the selected project only
  const teamMembers = useMemo(() => {
    if (!displayProject) return [];

    const members: any[] = [];
    const seen = new Set<string>();

    const processMembers = (categoryData: any, categoryName: string) => {
      const list = Array.isArray(categoryData) ? categoryData : [categoryData];
      list.forEach((member: any) => {
        if (member?.id && !seen.has(member.id)) {
          seen.add(member.id);
          members.push({
            id: member.id,
            fullName: member.fullname || member.fullName || member.name,
            photo: member.avatar || member.photo,
            position: member.position,
            category: categoryName,
            phone: member.phone || null,
            email: member.email || null,
          });
        }
      });
    };

    if (displayProject.team && typeof displayProject.team === "object") {
      Object.keys(displayProject.team).forEach((catName) => {
        processMembers(displayProject.team[catName], catName);
      });
    }

    return members;
  }, [displayProject]);

  return (
    <>
      <PortalHero
        eyebrow={displayProject?.name || "Active project"}
        title={firstName ? `Welcome, ${firstName}` : "Welcome"}
        summary="Your invoices, the people looking after the project, and the latest progress on site."
      />

      <div style={{ maxWidth: 1100, margin: "0 auto", paddingBottom: 64 }}>
      <Section
        title="For you"
        hint="The three places you will use most."
        className="ui-mt-0"
      >
        <Grid cols={3}>
          <Card
            to="/project-home/documents?section=photos"
            eyebrow="Project library"
            title="Site Photos"
            footer="Open in Documents"
          >
            The latest progress photos from the job site.
          </Card>
          <Card
            to="/project-home/documents?section=plans"
            eyebrow="Project library"
            title="Plans"
            footer="Open in Documents"
          >
            Drawings, models and the current design set.
          </Card>
          <Card
            to="/project-home/financial"
            eyebrow="Project"
            title="Financials"
            footer="Invoices, payments and the library"
          >
            Your invoices, payment history and financial documents.
          </Card>
        </Grid>
      </Section>

      <section className="ui-section">
        <PendingInvoicesComponent />
      </section>

      {recent.length > 0 && (
        <Section title="What's new" hint="Recently added to your project library.">
          <Grid cols={2}>
            {recent.map((item) => {
              const date = new Date(item.date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                timeZone: "UTC",
              });
              const footer = [item.groupLabel, (item.extension || "").toUpperCase()]
                .filter(Boolean)
                .join(" · ");
              return (
                <Card
                  key={item.id}
                  to={`/project-home/documents?section=${encodeURIComponent(item.sectionKey)}`}
                  eyebrow={item.sectionLabel}
                  title={item.displayName}
                  footer={footer || undefined}
                >
                  {date}
                </Card>
              );
            })}
          </Grid>
        </Section>
      )}

      {(fetchProjectsStatus === "loading" || teamMembers.length > 0) && (
        <Section title="Your team of experts">
          {fetchProjectsStatus === "loading" ? (
            <Grid cols={4}>
              {[0, 1, 2, 3].map((index) => (
                <Card key={index}>
                  <div className="ui-row">
                    <Skeleton style={{ width: 48, height: 48 }} />
                    <div className="ui-stack ui-stack--sm" style={{ flex: 1 }}>
                      <Skeleton style={{ width: 90, height: 14 }} />
                      <Skeleton style={{ width: 60, height: 10 }} />
                    </div>
                  </div>
                </Card>
              ))}
            </Grid>
          ) : (
            <Grid cols={4}>
              {teamMembers.map((member: any) => (
                <div key={member.id} className="ui-card">
                  <div className="ui-row" style={{ gap: 12 }}>
                    <Avatar src={member.photo} name={member.fullName} />
                    <div style={{ minWidth: 0 }}>
                      <div className="ui-card__title">{member.fullName}</div>
                      <div className="ui-eyebrow">
                        {member.position || member.category}
                      </div>
                    </div>
                  </div>
                  <div className="ui-stack ui-stack--sm" style={{ marginTop: 12 }}>
                    {member.phone && (
                      <a className="ui-caption ui-link" href={`tel:${member.phone}`}>
                        {member.phone}
                      </a>
                    )}
                    {member.email && (
                      <a
                        className="ui-caption ui-link ui-truncate"
                        href={`mailto:${member.email}`}
                      >
                        {member.email}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </Grid>
          )}
        </Section>
      )}

      <section className="ui-section">
        <SiteProgressCarousel
          projectId={displayProject?.id}
          fallbackImages={["/hero_bg_1.jpeg", "/hero_bg.jpeg"]}
        />
      </section>
      </div>
    </>
  );
};

export default ClientHome;
